import * as React from 'react';
import { Modal, ModalBody, ModalFooter, ModalSize, CustomButton, ButtonType, AlertMessage, AlertType, TextInput, Form, Textarea, CustomSubmitButton, CustomSubmitButtonType, HiddenInput, Table } from "soprasteria-ui-library";
import { useAuth } from '../../../App';
import { ICreatePreassessmentModel } from '../../../models/IPreAssessment';
import { setMessage } from '../../../helpers/MessageHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { IRiskPotReportPreAssessment } from '../../../models/IRiskPotReport';
import { RiskPotReportPreAssessmentApi } from '../../../api/RiskPotReportPreAssessmentApi';
import { dateTimeToDateString } from '../../../helpers/DateHelper';

interface IPreAssessmentModal {
    riskPotReportId: number;
    getRiskPotReport: () => void;
    selectedPreAssessment: IRiskPotReportPreAssessment | undefined;
    clearSelectedPreAssessment: () => void;
    changeSuccessMessage: (message: string) => void;
    hasRoleAllmantandvard: boolean;
    renderForm: boolean;
    setRenderForm: React.Dispatch<React.SetStateAction<boolean>>;
}

export const PreAssessmentModal: React.FunctionComponent<IPreAssessmentModal> = ({ riskPotReportId, getRiskPotReport, selectedPreAssessment, clearSelectedPreAssessment, changeSuccessMessage, hasRoleAllmantandvard, setRenderForm, renderForm }) => {
    const auth = useAuth();
    const api = new RiskPotReportPreAssessmentApi(auth);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>();

    React.useEffect(() => {
        setRenderForm(true);
    }, [selectedPreAssessment])

    const savePreAssessment = (data: ICreatePreassessmentModel) => {
        setIsLoading(true);
        api.savePreAssessment(data, () => {
            getRiskPotReport();
            changeSuccessMessage("Förhandsbedömningen har sparats");
            document.getElementById("close-create-pre-assessment-modal")?.click();
        }, (error: string) => {
            const message = setMessage(error, errorMessage ?? "");
            setErrorMessage(message);
        }, () => setIsLoading(false));
    }

    const sendPreAssessment = (data: ICreatePreassessmentModel) => {
        setIsLoading(true);
        api.sendPreAssessment(data, () => {
            getRiskPotReport();
            changeSuccessMessage("Förhandsbedömningen har skickats");
            document.getElementById("close-create-pre-assessment-modal")?.click();
        }, (error: string) => {
            const message = setMessage(error, errorMessage ?? "");
            setErrorMessage(message);
        }, () => setIsLoading(false));
    }

    const readDecisionButtonClick = () => {
        if (selectedPreAssessment) {
            setIsLoading(true);
            api.markPreAssessmentDecisionAsRead(selectedPreAssessment.preAssessmentId, () => {
                getRiskPotReport();
                changeSuccessMessage("Förhandsbedömningsbeslutet har lästs");
                document.getElementById("close-create-pre-assessment-modal")?.click();
            }, (error) => {
                const message = setMessage(error, errorMessage ?? "");
            setErrorMessage(message);
            }, () => setIsLoading(false));
        }
    }

    const setReadonly = (): boolean => {
        return selectedPreAssessment !== undefined && selectedPreAssessment.canBeEdited === false;
    }

    const clearForm = () => {
        setRenderForm(false);
        clearSelectedPreAssessment();
    }

    return (
        <Modal id="create-pre-assessment-modal" header="Förhandsbedömning" modalSize={ModalSize.large}>
            {
                renderForm ?
                    <Form>
                        <ModalBody>
                            <AlertMessage duration={3000} alertType={AlertType.danger}>{errorMessage}</AlertMessage>
                            <HiddenInput name="riskPotReportId" value={riskPotReportId.toString()} />
                            <HiddenInput name="preAssessmentId" value={selectedPreAssessment?.preAssessmentId.toString() ?? "0"} />
                            <Textarea name="motivation" label="Motivering för behandling" defaultValue={selectedPreAssessment?.motivation} readonly={setReadonly()} rows={6} inlineLabel required />
                            {
                                selectedPreAssessment?.status ?
                                    <TextInput name="readonlyStatus" label="Ärendestatus" defaultValue={selectedPreAssessment.status} readonly inlineLabel />
                                    : null
                            }
                            {
                                selectedPreAssessment?.decisionDate ?
                                    <TextInput name="readonlyDecisionDate" label="Beslutsdatum" defaultValue={dateTimeToDateString(selectedPreAssessment.decisionDate)} readonly inlineLabel />
                                    : null
                            }
                            {
                                selectedPreAssessment?.decisionComment ?
                                    <Textarea name="readonlyDecisionComment" label="Motivering till beslut" defaultValue={selectedPreAssessment.decisionComment} readonly inlineLabel />
                                    : null
                            }
                            {
                                selectedPreAssessment?.treatments && selectedPreAssessment.treatments.length > 0 ?
                                    <>
                                        <h5>Beslutade åtgärder</h5>
                                        <Table>
                                            <thead>
                                                <tr>
                                                    <th>Åtgärdskod</th>
                                                    <th>Beslut</th>
                                                    <th>Tand/käke</th>
                                                    <th>Tandyta</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    selectedPreAssessment.treatments.map((treatment, key) =>
                                                        <tr key={key}>
                                                            <td>{treatment.code}</td>
                                                            <td>{treatment.status}</td>
                                                            <td>{treatment.toothCode?.code}</td>
                                                            <td>{treatment.toothSurface}</td>
                                                        </tr>
                                                    )
                                                }
                                            </tbody>
                                        </Table>
                                    </>
                                    : null
                            }
                        </ModalBody>
                        <ModalFooter>
                            {
                                hasRoleAllmantandvard && selectedPreAssessment?.hasUnreadDecision ?
                                    <>
                                        <CustomSubmitButton isLoading={isLoading} onButtonSubmit={readDecisionButtonClick}>Markera som läst</CustomSubmitButton>
                                    </>
                                    : null
                            }
                            <CustomButton buttonType={ButtonType.general} buttonText="Avbryt" data-dismiss="modal" id="close-create-pre-assessment-modal" onClick={clearForm} />
                            {
                                setReadonly() === false ?
                                    <>
                                        <CustomSubmitButton buttonType={CustomSubmitButtonType.alternative} onButtonSubmit={savePreAssessment} isLoading={isLoading}><FontAwesomeIcon icon={faSave} /> Spara förhandsbedömning</CustomSubmitButton>
                                        <CustomSubmitButton buttonType={CustomSubmitButtonType.default} onButtonSubmit={sendPreAssessment} isLoading={isLoading}>Begär förhandsbedömning</CustomSubmitButton>
                                    </>
                                    : null
                            }
                        </ModalFooter>
                    </Form>
                    : null
            }
        </Modal >
    )
}